import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  ErrorUnexpected,
  OperationSuccessfully,
} from "../../../../../const/Messages";
import * as UserAccessPolicyService from "../../../../../service/UserAccessPolicyService.js";
import * as EmailDeliveryConfigurationService from "../../../../../service/EmailDeliveryConfigurationService.js";
import Loading from "../../../../Common/Loading/Loading.js";

export default function UserAccessPolicy() {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);

  const [values, setValues] = useState({
    accessType: "DEFAULT_PASSWORD",
    defaultPassword: "",
  });

  const [visibled, setVisibled] = useState(false);
  const [isSmtpConfigured, setIsSmtpConfigured] = useState(false);

  const schema = Yup.object().shape({
    accessType: Yup.string().required("Access type is a required field"),
    defaultPassword: Yup.string().when("accessType", {
      is: "DEFAULT_PASSWORD",
      then: Yup.string()
        .required(
          "Default password is required when access type is 'Default password'"
        )
        .min(4, "The default password must be at least 4 characters long")
        .max(20, "The default password must not exceed 20 characters"),
      otherwise: Yup.string().notRequired(),
    }),
  });

  useEffect(() => {
    Promise.all([
      UserAccessPolicyService.find(),
      EmailDeliveryConfigurationService.find().catch(() => null),
    ])
      .then(([userData, smtpData]) => {
        setValues({
          accessType: userData.accessType || "DEFAULT_PASSWORD",
          defaultPassword: userData.defaultPassword || "",
        });
        if (
          smtpData &&
          smtpData.active === true &&
          smtpData.validated === true
        ) {
          setIsSmtpConfigured(true);
        }
      })
      .catch((error) => {
        if (error) {
          toast.error(ErrorUnexpected);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  function onSubmit(values, actions) {
    const payload = {
      ...values,
      defaultPassword:
        values.accessType === "DEFAULT_PASSWORD"
          ? values.defaultPassword
          : null,
    };

    UserAccessPolicyService.merge(payload)
      .then(() => {
        toast.success(OperationSuccessfully);
        history.push("/settings");
      })
      .catch((error) => {
        if (!error || !error.response || !error.response.data) {
          toast.error(ErrorUnexpected);
        } else if (error.response.data.fields) {
          error.response.data.fields.forEach((field) => {
            actions.setFieldError(field.name, field.message);
          });
        } else {
          toast.error(ErrorUnexpected);
        }
      })
      .finally(() => actions.setSubmitting(false));
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Row>
      <Col>
        <div className="ibox">
          <div className="ibox-title">
            <h5>Setup - User Access Policy Configuration</h5>
          </div>
          <div className="ibox-content">
            <Formik
              initialValues={values}
              validationSchema={schema}
              onSubmit={onSubmit}
            >
              {({ isSubmitting, values }) => (
                <Form autoComplete="off">
                  <div className="form-group row align-items-center">
                    <label className="col-sm-12 col-md-4 col-lg-3 col-form-label font-weight-bold">
                      Access type:
                    </label>
                    <div className="col-sm-12 col-md-8 col-lg-9">
                      <div className="d-flex align-items-center mt-2 mt-md-0">
                        <div className="form-check form-check-inline">
                          <Field
                            id="accessDefault"
                            name="accessType"
                            className="form-check-input"
                            type="radio"
                            value="DEFAULT_PASSWORD"
                          />
                          <label
                            className="form-check-label mb-0"
                            htmlFor="accessDefault"
                          >
                            Default password
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <Field
                            disabled={!isSmtpConfigured}
                            id="accessEmail"
                            name="accessType"
                            className="form-check-input"
                            type="radio"
                            value="RANDOM_BY_EMAIL"
                          />
                          <label
                            className="form-check-label mb-0"
                            htmlFor="accessEmail"
                          >
                            Random password by e-mail
                          </label>
                        </div>
                      </div>
                      <ErrorMessage
                        name="accessType"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </div>
                  </div>

                  {values.accessType === "DEFAULT_PASSWORD" ? (
                    <div className="form-group row">
                      <label
                        htmlFor="defaultPassword"
                        className="col-form-label col-sm-12 col-md-4 col-lg-4 col-xl-3 col-xxl-1"
                      >
                        Default password:
                      </label>
                      <div className="col-sm-12 col-md-8 col-lg-6 col-xl-4">
                        <div className="input-group">
                          <Field
                            className="form-control"
                            name="defaultPassword"
                            type={visibled ? "text" : "password"}
                            placeholder="Password"
                            autoComplete="current-password"
                          />
                          <div
                            className="input-group-append"
                            onClick={() => setVisibled(!visibled)}
                          >
                            <span
                              className="input-group-text"
                              style={{ cursor: "pointer" }}
                            >
                              {visibled ? <FiEyeOff /> : <FiEye />}
                            </span>
                          </div>
                        </div>
                        <span className="text-danger">
                          <ErrorMessage name="defaultPassword"></ErrorMessage>
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="form-group row align-items-center">
                      <div className="col-sm-12 col-md-4 col-lg-3"></div>
                      <div className="col-sm-12 col-md-8 col-lg-9">
                        <Link
                          to="/settings/setup/email-delivery-configuration"
                          className="btn btn-link px-0"
                        >
                          SMTP Register to send e-mail
                        </Link>
                      </div>
                    </div>
                  )}

                  <div className="hr-line-dashed"></div>
                  <div className="form-group row">
                    <div className="col-sm-12 d-flex justify-content-end">
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Col>
    </Row>
  );
}
